import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { rememberReturnTo } from '../lib/returnTo';
import { usePricing } from '../hooks/usePricing';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import ProListingForm from '../components/ProListingForm';
import { Button } from '../components/ui/button';

/**
 * Edit an own listing (edit mode). Loads the listing from GET /api/pro-listings/mine and finds
 * it by :id (that endpoint returns the caller's own rows incl. drafts). Flag-gated like the
 * rest of the surface. On success, returns to the dashboard.
 */
const EditServiceListing = ({ profile, setProfile }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { pricing, loading: pricingLoading } = usePricing();
  const servicesEnabled = Boolean(pricing?.services_enabled);

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Flag-off during pricing load: leave loading=true — the (pricingLoading || loading) render
    // guard + coming-soon shell cover it; flipping loading here caused a not-found flash.
    if (!servicesEnabled) return undefined;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          // No session → send to auth with returnTo (replace, not push — this fires on mount,
          // so Back must not re-trigger it), matching ServiceRequestDetail's pattern. Stored via
          // rememberReturnTo: router state is lost across /auth → /sign-up and the auth reload.
          if (!cancelled) {
            rememberReturnTo(`/services/listings/${id}/edit`);
            navigate('/auth', { replace: true });
          }
          return;
        }
        const res = await api.get('/api/pro-listings/mine', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const mine = res.data?.listings || [];
        const found = mine.find((l) => l.id === id);
        if (cancelled) return;
        if (found) {
          setListing(found);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, servicesEnabled]);

  const handleSuccess = () => {
    navigate('/dashboard');
  };

  if (!pricingLoading && !servicesEnabled) {
    return (
      <div className="min-h-screen-safe pb-nav md:pb-0">
        <TopBar profile={profile} setProfile={setProfile} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">{t('services.comingSoonTitle')}</h1>
          <p className="text-muted-foreground">{t('services.comingSoonBody')}</p>
        </div>
        <BottomNav profile={profile} />
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">{t('services.editTitle')}</h1>
          <p className="text-muted-foreground">{t('services.editSubtitle')}</p>
        </div>
        {(pricingLoading || loading) ? (
          <div className="flex justify-center py-16">
            <div className="spinner" role="status" aria-label={t('common.loading', 'Loading...')} />
          </div>
        ) : notFound || !listing ? (
          <div className="py-16 text-center">
            <h2 className="text-xl font-semibold mb-3">{t('services.detailNotFoundTitle')}</h2>
            <p className="text-muted-foreground mb-6">{t('services.errorNotFound')}</p>
            <Button onClick={() => navigate('/dashboard')}>{t('services.backToDashboard')}</Button>
          </div>
        ) : (
          <ProListingForm mode="edit" initial={listing} onSuccess={handleSuccess} />
        )}
      </div>
      <BottomNav profile={profile} />
    </div>
  );
};

export default EditServiceListing;
